import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dropdown, DropdownTrigger, DropdownMenu, DropdownItem, Chip, Avatar, Spinner } from '@heroui/react';
import AccountTable from '../../../components/AccountTable';
import { IdView } from '../../../components';
import { useTranslations } from '../../../contexts/translation.context';
import { WithdrawalRequest } from '../types';
import { formatAmount, formatDate } from '../utils';
import { STATUS_COLORS, ROLE_COLORS } from '../constants';
import { IcDate } from '../../../components/icons';
import { formatTimeAgo } from '../../../utils/formatTimeAgo';

interface WithdrawalTableProps {
  withdrawals: WithdrawalRequest[];
  isLoading: boolean;
  totalCount: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onStatusChange?: (id: string, status: 'paid' | 'pending' | 'cancel') => void;
}

const WithdrawalTable: React.FC<WithdrawalTableProps> = ({
  withdrawals,
  isLoading,
  totalCount,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onStatusChange,
}) => {
  const { t } = useTranslations();
  const navigate = useNavigate();

  const columns = useMemo(() => [
    { key: 'id', name: t('withdrawal.id') || 'ID' },
    { key: 'account', name: t('withdrawal.account') },
    { key: 'telegram', name: 'Telegram' },
    { key: 'amount', name: t('withdrawal.amount') },
    { key: 'status', name: t('withdrawal.status') },
    { key: 'date', name: t('withdrawal.date') },
    { key: 'actions', name: t('withdrawal.actions') },
  ], [t]);

  const handleAction = useCallback((action: string, withdrawalId: string) => {
    switch (action) {
      case 'paid':
      case 'pending':
      case 'cancel':
        onStatusChange?.(withdrawalId, action as 'paid' | 'pending' | 'cancel');
        break;
    }
  }, [onStatusChange]);

  const renderCell = useCallback((withdrawal: WithdrawalRequest, columnKey: string) => {
    switch (columnKey) {
      case 'id':
        return (
          <div className="flex py-[8px]">
            <IdView textToCopy={withdrawal.id}>
              {withdrawal.id.slice(-4)}
            </IdView>
          </div>
        );

      case 'account':
        const shortenedAddress = withdrawal.address.length > 20
          ? `${withdrawal.address.substring(0, 10)}...${withdrawal.address.substring(withdrawal.address.length - 8)}`
          : withdrawal.address;
        return (
          <div className="flex items-center gap-[12px]">
            <Avatar
              src={withdrawal.owner.avatar}
              name={withdrawal.owner.name}
              size="sm"
              className="flex-shrink-0"
            />
            <div className="flex flex-col items-start gap-[4px]">
              <div className="flex items-center gap-[8px]">
                <span
                  className="text-[14px] font-medium text-[#006FEE] cursor-pointer hover:underline"
                  onClick={() => navigate(`/users/partners/${withdrawal.owner.id}/balance`)}
                >
                  {withdrawal.owner.name}
                </span>
                {withdrawal.owner.lvl && (
                  <Chip
                    style={{
                      backgroundColor: ROLE_COLORS[withdrawal.owner.lvl]?.bg || '#EEEEEF',
                      color: ROLE_COLORS[withdrawal.owner.lvl]?.text || '#71717A',
                    }}
                    className="h-[20px] px-[8px] text-[12px] font-medium capitalize"
                  >
                    {withdrawal.owner.lvl}
                  </Chip>
                )}
              </div>
              <IdView textToCopy={withdrawal.owner.id}>
                {withdrawal.owner.id.slice(-4)}
              </IdView>
              <IdView textToCopy={withdrawal.address}>
                {shortenedAddress}
              </IdView>
            </div>
          </div>
        );

      case 'telegram':
        return (
          <div className="flex items-center gap-[4px]">
            {withdrawal.owner.tg_tag ? (
              <>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 0C3.58 0 0 3.58 0 8C0 12.42 3.58 16 8 16C12.42 16 16 12.42 16 8C16 3.58 12.42 0 8 0ZM11.93 5.48L10.68 11.24C10.59 11.64 10.35 11.74 10.01 11.54L7.95 10.04L6.95 11.01C6.85 11.11 6.76 11.2 6.56 11.2L6.7 9.11L10.45 5.72C10.61 5.58 10.42 5.5 10.21 5.64L5.63 8.58L3.6 7.95C3.18 7.82 3.17 7.55 3.68 7.35L11.41 4.35C11.76 4.22 12.06 4.43 11.93 5.48Z" fill="#229ED9"/>
                </svg>
                <span className="text-[14px] text-[#11181C]">
                  {withdrawal.owner.tg_tag}
                </span>
              </>
            ) : (
              <span className="text-[14px] text-[#71717A]">-</span>
            )}
          </div>
        );

      case 'amount':
        return (
          <span className="text-[14px] font-semibold text-[#11181C]">
            ${parseFloat(withdrawal.amount).toFixed(2)}
          </span>
        );

      case 'status':
        const statusConfig = STATUS_COLORS[withdrawal.status] || STATUS_COLORS.pending;
        const statusLabel = t(`withdrawal.${withdrawal.status}`) || statusConfig.label;
        return (
          <Chip
            style={{
              backgroundColor: statusConfig.bg,
              color: statusConfig.text,
            }}
            className="h-[24px] px-[12px] text-[12px] font-medium"
          >
            {statusLabel}
          </Chip>
        );

      case 'date':
        return (
          <span className="text-[14px] flex items-center whitespace-nowrap text-[#71717A]">
            <IcDate/>
            {formatTimeAgo(withdrawal.created_at,t)}
          </span>
        );

      case 'actions':
        return (
          <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
            <Dropdown>
              <DropdownTrigger>
                <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="8" cy="2.5" r="1.5" fill="#71717A"/>
                    <circle cx="8" cy="8" r="1.5" fill="#71717A"/>
                    <circle cx="8" cy="13.5" r="1.5" fill="#71717A"/>
                  </svg>
                </button>
              </DropdownTrigger>
              <DropdownMenu
                aria-label="Actions"
                onAction={(key: React.Key) => handleAction(String(key), withdrawal.id)}
              >
                {withdrawal.status !== 'paid' && (
                  <DropdownItem key="paid" className="text-success" color="success">
                    {t('withdrawal.markAsPaid')}
                  </DropdownItem>
                )}
                {withdrawal.status !== 'cancel' && (
                  <DropdownItem key="cancel" className="text-danger" color="danger">
                    {t('withdrawal.cancel')}
                  </DropdownItem>
                )}
              </DropdownMenu>
            </Dropdown>
          </div>
        );

      default:
        return null;
    }
  }, [t, handleAction]);

  if (isLoading && withdrawals.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spinner size="lg" color="primary" />
      </div>
    );
  }

  return (
    <AccountTable
      data={withdrawals}
      columns={columns}
      renderCell={renderCell}
      isSearchEmpty={withdrawals.length === 0 && !isLoading}
      emptyContent={t('withdrawal.noData')}
      pagination={{
        page: currentPage,
        page_size: pageSize,
        count: totalCount,
      }}
      currentPage={currentPage}
      currentPageSize={pageSize}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
    />
  );
};

export default WithdrawalTable;
